/**
 * Score badge — Fiable ou Pas
 * Risk color system:
 *   Low  (0–29):  teal   #2F6B5F — bg #E7F3EF
 *   Med (30–59): amber  #B7791F — bg #FFF4E5
 *   High (60+):  red    #B42318 — bg #FEECEC
 */
export function getScoreConfig(score, riskLevel) {
  if (riskLevel === "site_inaccessible") return {
    level: "site_inaccessible",
    label: "Site inaccessible",
    color: "#B7791F",
    bg: "#FFF4E5",
    border: "#F6D58C",
    ring: "#B7791F",
    textClass: "text-[#B7791F]",
    bgClass: "bg-[#FFF4E5] border-[#F6D58C]",
  };
  if (riskLevel === "analyse_partielle") return {
    level: "analyse_partielle",
    label: "Analyse partielle",
    color: "#4B5563", // Gray
    bg: "#F8FAFC",
    border: "#CBD5E1",
    ring: "#94A3B8",
    textClass: "text-[#4B5563]",
    bgClass: "bg-[#F8FAFC] border-[#CBD5E1]",
  };
  if (score < 30) return {
    level: "risque_eleve",
    label: "Risque élevé",
    color: "#B42318",
    bg: "#FEECEC",
    border: "#F9BBBB",
    ring: "#B42318",
    textClass: "text-[#B42318]",
    bgClass: "bg-[#FEECEC] border-[#F9BBBB]",
  };
  if (score < 60) return {
    level: "vigilance",
    label: "Vigilance recommandée",
    color: "#B7791F",
    bg: "#FFF4E5",
    border: "#F6D58C",
    ring: "#B7791F",
    textClass: "text-[#B7791F]",
    bgClass: "bg-[#FFF4E5] border-[#F6D58C]",
  };
  return {
    level: "risque_faible",
    label: "Fiable / rassurant",
    color: "#2F6B5F",
    bg: "#E7F3EF",
    border: "#A7D4C7",
    ring: "#2F6B5F",
    textClass: "text-[#2F6B5F]",
    bgClass: "bg-[#E7F3EF] border-[#A7D4C7]",
  };
}

export default function ScoreBadge({ score, riskLevel, size = "lg" }) {
  const cfg = getScoreConfig(score, riskLevel);
  const radius = 54;
  const circ = 2 * Math.PI * radius;
  const progress = circ - (score / 100) * circ;

  if (size === "sm") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border ${cfg.bgClass}`}
        data-testid="score-badge-small"
      >
        <span className="text-base font-bold" style={{ color: cfg.color }}>{score}</span>
        <span className="text-[#6B7280]">/100</span>
        <span className="hidden sm:inline" style={{ color: cfg.color }}>— {cfg.label}</span>
      </span>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4" data-testid="score-badge">
      {/* Circular gauge */}
      <div className="relative">
        <svg width="160" height="160" className="-rotate-90" aria-hidden="true">
          <circle
            cx="80" cy="80" r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="10"
          />
          <circle
            cx="80" cy="80" r={radius}
            fill="none"
            stroke={cfg.ring}
            strokeWidth="10"
            strokeDasharray={circ}
            strokeDashoffset={progress}
            strokeLinecap="round"
            className="score-ring-progress"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className="text-4xl font-black font-heading leading-none"
            style={{ color: cfg.color }}
          >
            {score}
          </span>
          <span className="text-sm text-[#6B7280] font-medium mt-0.5">/100</span>
        </div>
      </div>

      {/* Risk label badge */}
      <span
        className={`px-4 py-1.5 rounded-full text-sm font-bold border ${cfg.bgClass}`}
        style={{ color: cfg.color }}
      >
        {cfg.label}
      </span>
    </div>
  );
}
